import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { auth, db, functions, storage } from './firebase'; // Assuming firebase.js is in src
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, serverTimestamp, doc, onSnapshot, getDoc, query, where, getDocs, orderBy } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from 'firebase/functions'; // Ensure this is imported
import ActivityMap from './ActivityMap'; // Import the ActivityMap component
import { usePostHog } from 'posthog-js/react';
import { useWaterBasedStyles } from './hooks/useWaterBasedStyles'; // Import water-based styles hook
// import { httpsCallable } from 'firebase/functions'; // If needed for other operations

// Import styles - you might want to create a separate CSS file for this page later
// For now, you can reuse some from App.css or add inline/component-scoped styles.
import './RequestGeneratorPage.css'; // Import the new dedicated CSS file

// Prompt constants are removed as templates are fetched from backend

function RequestGeneratorPage() {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false); // Add admin state
    const { stravaId: stravaIdFromParams } = useParams();
    const location = useLocation(); // Get location object for query params
    const [stravaId, setStravaId] = useState('');
    const [prompt, setPrompt] = useState('');
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isLoading, setIsLoading] = useState(false); // For form submission loading

    // State for prompt templates
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [templateError, setTemplateError] = useState('');
    const [currentTemplateDetails, setCurrentTemplateDetails] = useState(null); // Stores full details of selected template

    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [requestStatus, setRequestStatus] = useState('');
    const [gpxFileLink, setGpxFileLink] = useState('');
    const [originalImageSrc, setOriginalImageSrc] = useState('');
    const [generatedImageSrc, setGeneratedImageSrc] = useState('');
    const [imageDisplayVisible, setImageDisplayVisible] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [sliderValue, setSliderValue] = useState(50);
    const [isDownloading, setIsDownloading] = useState(false); // New state for download button
    const [previousGenerations, setPreviousGenerations] = useState([]);
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
    const [previousGenerationsError, setPreviousGenerationsError] = useState(''); // New state for errors
    const [currentActivity, setCurrentActivity] = useState(null); // State for current activity data
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);

    // Water analysis state
    const [waterAnalysis, setWaterAnalysis] = useState(null);
    const [waterThreshold, setWaterThreshold] = useState(5.0);

    // Progress tracking
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
    const [averageGenerationTime, setAverageGenerationTime] = useState(120); // Default 2 minutes
    const [generationStartTime, setGenerationStartTime] = useState(null);

    const navigate = useNavigate();
    const imageComparisonDivRef = useRef(null); // For drag events
    const animationFrameIdRef = useRef(null); // For animation
    const isDraggingRef = useRef(false); // For drag state
    const posthog = usePostHog();

    // Use the water-based styles hook for filtering
    const {
        defaultStyles: filteredTemplates,
        recommendedStyles,
        waterSummary,
        isWaterAnalyzed
    } = useWaterBasedStyles(availableTemplates, waterAnalysis, waterThreshold);

    // Load water threshold from settings (similar to LandingPage)
    useEffect(() => {
        const loadWaterThreshold = async () => {
            try {
                const settingsDocRef = doc(db, 'settings', 'mapGeneration');
                const settingsDoc = await getDoc(settingsDocRef);
                if (settingsDoc.exists()) {
                    const settingsData = settingsDoc.data();
                    if (settingsData?.waterThreshold && typeof settingsData.waterThreshold === 'number') {
                        setWaterThreshold(settingsData.waterThreshold);
                    }
                }
            } catch (error) {
                console.error('Error loading water threshold:', error);
                // Keep default value of 5.0
            }
        };

        loadWaterThreshold();
    }, []);

    // Handler for water analysis results from ActivityMap
    const handleWaterAnalysis = (analysis) => {
        setWaterAnalysis(analysis);
        if (localStorage.getItem('waterDetectionDebug') === 'true') {
            console.log('RequestGeneratorPage: Water analysis received:', analysis);
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const actualReqId = queryParams.get('reqId');

        if (actualReqId) {
            setCurrentRequestId(actualReqId);
            const docRef = doc(db, "GenerationRequests", actualReqId);
            getDoc(docRef).then(docSnap => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.StravaId) setStravaId(data.StravaId);
                    if (data.promptTemplateId) {
                        setSelectedTemplateId(data.promptTemplateId);
                    } else if (data.prompt) {
                        setPrompt(data.prompt);
                        setSelectedTemplateId('');
                    }
                }
            }).catch(err => console.error("Error pre-filling from reqId:", err));
        } else if (stravaIdFromParams && stravaIdFromParams !== '_new') {
            setStravaId(stravaIdFromParams);
            fetchPreviousGenerations(stravaIdFromParams); // Fetch previous generations
            setPrompt('');
            setSelectedTemplateId('');
            setCurrentRequestId(null);
        } else {
            setStravaId('');
            setPrompt('');
            setSelectedTemplateId('');
            setCurrentRequestId(null);
            setPreviousGenerations([]); // Clear previous if no stravaId
            setCurrentActivity(null); // Clear activity
        }
        
        // Reset water analysis when stravaId changes
        setWaterAnalysis(null);
    }, [stravaIdFromParams, location.search]);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                // Check if user is admin
                try {
                    const idTokenResult = await currentUser.getIdTokenResult();
                    setIsAdmin(idTokenResult.claims.admin === true);
                } catch (error) {
                    console.error("Error checking admin status:", error);
                    setIsAdmin(false);
                }
                fetchPromptTemplates();
                fetchGenerationStats(); // Fetch generation stats when user is authenticated
            } else {
                setAvailableTemplates([]);
                setIsAdmin(false);
            }
        });
        return () => {
            unsubscribeAuth();
        };
    }, []);

    const fetchPromptTemplates = async () => {
        if (!auth.currentUser) return; // Ensure user is available for consistency, though parent `useEffect` checks
        setIsLoadingTemplates(true);
        setTemplateError('');
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data && Array.isArray(result.data.templates)) {
                setAvailableTemplates(result.data.templates);
            } else {
                setAvailableTemplates([]);
                setTemplateError('Could not load prompt templates format.');
            }
        } catch (error) {
            console.error("Error fetching prompt templates:", error);
            setTemplateError(`Failed to load templates: ${error.message}`);
            setAvailableTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    // Fetch generation time statistics
    const fetchGenerationStats = async () => {
        try {
            console.log('[RequestGeneratorPage] Fetching generation time stats...');
            const getGenerationTimeStats = httpsCallable(functions, 'getGenerationTimeStats');
            const result = await getGenerationTimeStats();
            const stats = result.data;
            console.log('[RequestGeneratorPage] Generation stats received:', stats);
            console.log('[RequestGeneratorPage] Average duration from backend:', stats.averageDurationSeconds);
            console.log('[RequestGeneratorPage] Sample size:', stats.sampleSize);
            console.log('[RequestGeneratorPage] Last updated:', stats.lastUpdated);

            const newAverageTime = stats.averageDurationSeconds || 120;
            console.log('[RequestGeneratorPage] Setting average generation time to:', newAverageTime);
            setAverageGenerationTime(newAverageTime);
        } catch (error) {
            console.error('[RequestGeneratorPage] Error fetching generation stats:', error);
            console.log('[RequestGeneratorPage] Using default average generation time: 120 seconds');
            // Keep default value
        }
    };

    // Fetch model-specific generation time statistics
    const fetchModelSpecificStats = async (model) => {
        try {
            console.log('[RequestGeneratorPage] Fetching model-specific stats for:', model);
            const getGenerationTimeStatsByModel = httpsCallable(functions, 'getGenerationTimeStatsByModel');
            const result = await getGenerationTimeStatsByModel({ model });
            const stats = result.data;
            console.log('[RequestGeneratorPage] Model-specific stats received:', stats);
            
            if (stats[model]) {
                const modelAverageTime = stats[model].averageDurationSeconds || 120;
                console.log('[RequestGeneratorPage] Model average duration:', modelAverageTime);
                console.log('[RequestGeneratorPage] Model sample size:', stats[model].sampleSize);
                setAverageGenerationTime(modelAverageTime);
            } else {
                console.log('[RequestGeneratorPage] No stats for model, using default');
                await fetchGenerationStats(); // Fall back to overall stats
            }
        } catch (error) {
            console.error('[RequestGeneratorPage] Error fetching model-specific stats:', error);
            await fetchGenerationStats(); // Fall back to overall stats
        }
    };

    // Calculate progress based on elapsed time and status
    const calculateProgress = (status, startTime, currentTime) => {
        if (!startTime || !currentTime) return 0;

        const elapsedSeconds = (currentTime - startTime.toDate().getTime()) / 1000;
        const progressRatio = Math.min(elapsedSeconds / averageGenerationTime, 0.95); // Cap at 95% until complete

        // Adjust progress based on status
        let statusProgress = 0;
        switch (status) {
            case 'fetching_activity_details':
            case 'initiating_gpx_download':
                statusProgress = 0.02;
                break;
            case 'gpx_downloading_authenticated':
            case 'gpx_downloading_unauthenticated':
                statusProgress = 0.04;
                break;
            case 'geodata_created':
                statusProgress = 0.06;
                break;
            case 'mapbox_generating':
                statusProgress = 0.08;
                break;
            case 'mapbox_generated':
                statusProgress = 0.10;
                break;
            case 'openai_processing':
                statusProgress = 0.15; // AI processing is the longest step, so keep it low
                break;
            case 'openai_processing_complete':
                return 100;
            default:
                statusProgress = 0;
        }

        // Use the higher of time-based or status-based progress
        return Math.max(statusProgress * 100, progressRatio * 100);
    };

    // Calculate estimated time remaining
    const calculateETA = (progress, startTime, currentTime) => {
        if (!startTime || !currentTime || progress >= 100) return null;

        const elapsedSeconds = (currentTime - startTime.toDate().getTime()) / 1000;

        console.log('[RequestGeneratorPage] ETA Calculation:', {
            progress: progress,
            elapsedSeconds: elapsedSeconds,
            averageGenerationTime: averageGenerationTime
        });

        // Always use average generation time for consistent ETA
        const remainingTime = Math.max(0, averageGenerationTime - elapsedSeconds);

        console.log('[RequestGeneratorPage] Using average time method:', {
            averageGenerationTime: averageGenerationTime,
            elapsedSeconds: elapsedSeconds,
            remainingTime: remainingTime
        });

        // Don't show ETA if remaining time is less than 5 seconds
        if (remainingTime < 5) {
            console.log('[RequestGeneratorPage] ETA too small, returning null');
            return null;
        }

        // Add 10% buffer to make estimates more conservative
        const bufferedETA = Math.round(remainingTime * 1.1);
        console.log('[RequestGeneratorPage] Final ETA (with 10% buffer):', bufferedETA);
        return bufferedETA;
    };

    // Helper to format duration from seconds
    const formatDuration = (totalSeconds) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}min`;
        } else if (minutes > 0) {
            return `${minutes} min`;
        } else {
            return `${seconds} sec`;
        }
    };

    useEffect(() => {
        if (selectedTemplateId && selectedTemplateId !== 'custom' && selectedTemplateId !== '') {
            const selected = filteredTemplates.find(t => t.id === selectedTemplateId);
            setCurrentTemplateDetails(selected || null);
            setPrompt(''); // Clear for additional notes or custom additions
            
            // Fetch model-specific generation stats
            if (selected && selected.generationModel) {
                fetchModelSpecificStats(selected.generationModel);
            } else {
                // Fall back to overall stats if no model specified
                fetchGenerationStats();
            }
        } else {
            setCurrentTemplateDetails(null);
            if (selectedTemplateId === 'custom') setPrompt('');
            // Use overall stats for custom prompts
            fetchGenerationStats();
        }
    }, [selectedTemplateId, filteredTemplates]);

    useEffect(() => {
        if (!currentRequestId) {
            setRequestStatus(''); setGpxFileLink(''); setOriginalImageSrc('');
            setGeneratedImageSrc(''); setImageDisplayVisible(false); setStatusError('');
            return;
        }
        const docRef = doc(db, "GenerationRequests", currentRequestId);
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setRequestStatus(data.status || 'N/A');
                setStatusError(data.error || '');

                // Update progress tracking
                if (data.generationStartTime && data.status !== 'openai_processing_complete') {
                    const currentTime = new Date();
                    const progress = calculateProgress(data.status, data.generationStartTime, currentTime);
                    const eta = calculateETA(progress, data.generationStartTime, currentTime);

                    setProgressPercentage(progress);
                    setEstimatedTimeRemaining(eta);
                    setGenerationStartTime(data.generationStartTime);
                } else if (data.status === 'openai_processing_complete') {
                    setProgressPercentage(100);
                    setEstimatedTimeRemaining(null);
                }

                if (data.gpxPath) {
                    try { setGpxFileLink(await getDownloadURL(ref(storage, data.gpxPath))); } catch (e) { console.error("GPX URL Error", e); setGpxFileLink(''); }
                }
                if (data.originalImagePath && data.generatedImagePath) {
                    try {
                        const origUrl = await getDownloadURL(ref(storage, data.originalImagePath));
                        const genUrl = await getDownloadURL(ref(storage, data.generatedImagePath));
                        let imagesLoaded = 0;
                        const totalImages = 2;
                        const onImageLoad = () => {
                            imagesLoaded++;
                            if (imagesLoaded === totalImages) {
                                setOriginalImageSrc(origUrl);
                                setGeneratedImageSrc(genUrl);
                                setImageDisplayVisible(true);
                                if (typeof animateImageComparison === 'function') animateImageComparison();
                            }
                        };
                        const img1 = new Image(); img1.onload = onImageLoad; img1.onerror = () => { console.error("Original image failed to load on gen page"); onImageLoad(); }; img1.src = origUrl;
                        const img2 = new Image(); img2.onload = onImageLoad; img2.onerror = () => { console.error("Generated image failed to load on gen page"); onImageLoad(); }; img2.src = genUrl;
                    } catch (e) { console.error("Image URL Error on gen page", e); setImageDisplayVisible(false); }
                } else { setImageDisplayVisible(false); }
            } else {
                setRequestStatus('Request details not found.');
                setStatusError('Document might have been deleted or ID is incorrect.');
            }
        });
        return () => unsubscribe();
    }, [currentRequestId]);

    // Update progress in real-time while generation is in progress
    useEffect(() => {
        let interval;

        if (generationStartTime && requestStatus && requestStatus !== 'openai_processing_complete') {
            interval = setInterval(() => {
                const currentTime = new Date();
                const progress = calculateProgress(requestStatus, generationStartTime, currentTime);
                const eta = calculateETA(progress, generationStartTime, currentTime);

                setProgressPercentage(progress);
                setEstimatedTimeRemaining(eta);
            }, 1000); // Update every second
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [generationStartTime, requestStatus, averageGenerationTime]);

    const handleSubmitRequest = async () => {
        if (!user) { setSubmitError("You must be signed in."); return; }
        if (!stravaId.trim() || !/^[0-9]+$/.test(stravaId.trim())) { setSubmitError("Valid Strava ID required."); return; }

        const isTemplateSelected = selectedTemplateId && selectedTemplateId !== 'custom' && selectedTemplateId !== '';
        const customText = prompt.trim();

        if (!isTemplateSelected && !customText) {
            setSubmitError("Please select a template or write a custom prompt."); return;
        }

        setIsLoading(true); setSubmitError(''); setSubmitMessage('');

        const requestPayload = {
            StravaId: stravaId.trim(),
            userId: user.uid,
            status: "request_received", // New generic initial status
            createdAt: serverTimestamp(),
            gpxDataSource: 'polyline', // Default to polyline for now. Add UI choice later if needed.
        };

        if (isTemplateSelected) {
            requestPayload.promptTemplateId = selectedTemplateId;
            if (customText) {
                requestPayload.customPromptText = customText;
            }
        } else {
            requestPayload.prompt = customText;
        }

        try {
            const docDataRef = await addDoc(collection(db, "GenerationRequests"), requestPayload);
            setSubmitMessage(`New request ${docDataRef.id} submitted. Monitoring...`);
            setCurrentRequestId(docDataRef.id);
            posthog?.capture('map_generation_request_success', {
                request_id: docDataRef.id,
                activity_id: stravaId
            });
        } catch (e) {
            let message = "Error submitting request.";
            if (e instanceof Error) {
                message = e.message;
            } else if (typeof e === 'string') {
                message = e;
            } else {
                // Handle other types of e if necessary, or keep a generic message
                console.error("An unknown error object was caught:", e);
            }
            setSubmitError(`Error: ${message}`);
            posthog?.capture('map_generation_request_failed', {
                error_message: message,
                activity_id: stravaId
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Animation and Drag Logic for image comparison (ensure these are defined or imported)
    const animateSlider = (targetValue, duration, onComplete = () => { }) => {
        if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
        const startValue = sliderValue;
        const startTime = performance.now();
        function frame(currentTime) {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            const currentValue = startValue + (targetValue - startValue) * progress;
            setSliderValue(currentValue);
            updateClipPath(currentValue);
            if (progress < 1) animationFrameIdRef.current = requestAnimationFrame(frame);
            else { animationFrameIdRef.current = null; onComplete(); }
        }
        animationFrameIdRef.current = requestAnimationFrame(frame);
    };
    const animateImageComparison = () => {
        setSliderValue(0); updateClipPath(0);
        if (imageComparisonDivRef.current) imageComparisonDivRef.current.style.pointerEvents = 'none';
        const sliderEl = document.getElementById('imageOverlaySliderReact');
        if (sliderEl) sliderEl.disabled = true;
        setTimeout(() => {
            animateSlider(100, 1000, () => {
                setTimeout(() => {
                    animateSlider(50, 500, () => {
                        if (imageComparisonDivRef.current) imageComparisonDivRef.current.style.pointerEvents = 'auto';
                        if (sliderEl) sliderEl.disabled = false;
                    });
                }, 200);
            });
        }, 100);
    };
    const updateClipPath = (percentage) => {
        const generatedImg = document.getElementById('generatedImageDisplayForSlider');
        if (generatedImg) {
            generatedImg.style.clipPath = `polygon(0 0, 100% 0, 100% ${percentage}%, 0 ${percentage}%)`;
        }
    };
    const handleSliderChange = (event) => {
        if (animationFrameIdRef.current) { cancelAnimationFrame(animationFrameIdRef.current); animationFrameIdRef.current = null; }
        setSliderValue(parseFloat(event.target.value));
        updateClipPath(event.target.value);
    };
    const startDrag = (e) => {
        if (animationFrameIdRef.current) { cancelAnimationFrame(animationFrameIdRef.current); animationFrameIdRef.current = null; }
        isDraggingRef.current = true;
        if (e.type === 'touchstart' && e.cancelable) e.preventDefault();
    };
    const onDrag = (e) => {
        if (!isDraggingRef.current || !imageComparisonDivRef.current) return;
        const event = e.touches ? e.touches[0] : e;
        const rect = imageComparisonDivRef.current.getBoundingClientRect();
        let y = event.clientY - rect.top;
        let percentage = (y / rect.height) * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        setSliderValue(percentage);
        updateClipPath(percentage);
        if (e.type === 'touchmove' && e.cancelable) e.preventDefault();
    };
    const stopDrag = () => { isDraggingRef.current = false; };
    useEffect(() => {
        const div = imageComparisonDivRef.current;
        if (div && imageDisplayVisible) {
            document.addEventListener('mousemove', onDrag);
            document.addEventListener('touchmove', onDrag, { passive: false });
            document.addEventListener('mouseup', stopDrag);
            document.addEventListener('touchend', stopDrag);
            return () => {
                document.removeEventListener('mousemove', onDrag);
                document.removeEventListener('touchmove', onDrag);
                document.removeEventListener('mouseup', stopDrag);
                document.removeEventListener('touchend', stopDrag);
            };
        }
    }, [imageDisplayVisible]);

    const handleDownloadAiImage = async () => {
        if (!generatedImageSrc || !currentRequestId) {
            alert("Generated image URL is not available yet.");
            return;
        }

        // Check if device is iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
            // For iOS devices: Redirect to special iOS download page
            let iosUrl = `/ios-download/${currentRequestId}?imageUrl=${encodeURIComponent(generatedImageSrc)}`;

            // Only add Strava ID if it exists
            if (stravaId) {
                iosUrl += `&stravaId=${stravaId}`;
            }

            window.location.href = iosUrl;
            return;
        }

        // For non-iOS devices: Continue with normal download process
        setIsDownloading(true);
        try {
            const response = await fetch(generatedImageSrc);
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `mythic_map_ai_${currentRequestId}.png`;
            document.body.appendChild(link); // Required for Firefox
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);

            // After download, show the dialog for Strava
            if (stravaId && window.confirm("AI Image downloaded!\n\nWould you like to add it to your Strava activity now?\n(This will open the Strava activity edit page.)")) {
                const stravaEditUrl = `https://www.strava.com/activities/${stravaId}/edit`;
                window.open(stravaEditUrl, '_blank');
                alert("Please manually upload the downloaded image (mythic_map_ai_" + currentRequestId + ".png) to your Strava activity.\n\nYou should find the image in your Downloads folder.");
            }
        } catch (error) {
            console.error("Error downloading AI image:", error);
            alert(`Failed to download image: ${error.message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    const fetchPreviousGenerations = async (activityId) => {
        if (!activityId) return;
        setIsLoadingPrevious(true);
        setPreviousGenerationsError(''); // Clear previous errors
        try {
            // Ensure httpsCallable is imported from 'firebase/functions'
            const getPreviousGenerationsFunc = httpsCallable(functions, 'getPreviousGenerationsByStravaId');
            const result = await getPreviousGenerationsFunc({ activityId });

            if (result.data && Array.isArray(result.data.generations)) {
                // The Cloud Function now returns createdAt as a pre-formatted string
                // and generatedImageUrl as a signed URL.
                setPreviousGenerations(result.data.generations);
            } else {
                console.error("Unexpected response structure from getPreviousGenerationsByStravaId:", result);
                setPreviousGenerationsError("Failed to load previous generations: Unexpected response format.");
                setPreviousGenerations([]);
            }
        } catch (error) {
            console.error("Error fetching previous generations via Cloud Function:", error);
            // Firebase callable functions errors have a 'message' property.
            const errorMessage = error.message || "An unknown error occurred.";
            setPreviousGenerationsError(`Error fetching previous generations: ${errorMessage}`);
            setPreviousGenerations([]); // Clear previous generations on error
        } finally {
            setIsLoadingPrevious(false);
        }
    };

    const fetchActivityData = async (activityId) => {
        if (!activityId || !user) return;

        setIsLoadingActivity(true);
        try {
            // First, try to get the activity from the user's recent activities
            const getActivitiesFunc = httpsCallable(functions, 'getStravaActivities');
            const result = await getActivitiesFunc();

            if (result.data && Array.isArray(result.data)) {
                // Find the specific activity
                const activity = result.data.find(act => act.id.toString() === activityId.toString());
                if (activity) {
                    setCurrentActivity(activity);
                } else {
                    // Activity not found in recent activities
                    console.log("Activity not found in recent activities");
                    setCurrentActivity(null);
                }
            }
        } catch (error) {
            console.error("Error fetching activity data:", error);
            setCurrentActivity(null);
        } finally {
            setIsLoadingActivity(false);
        }
    };

    const renderPreviousGenerations = () => {
        if (isLoadingPrevious) {
            return <p>Loading previous maps...</p>;
        } else if (previousGenerationsError) {
            return <p className="error">{previousGenerationsError}</p>;
        } else if (previousGenerations.length === 0) {
            return <p>No maps previously generated for this activity.</p>;
        } else {
            return (
                <div className="previous-generations-grid">
                    {previousGenerations.map(gen => (
                        <div key={gen.id} className="previous-generation-card">
                            {gen.generatedImageUrl ? (
                                <img src={gen.generatedImageUrl} alt={`Generated map for ${gen.id}`} className="previous-gen-thumbnail" />
                            ) : (
                                <div className="previous-gen-thumbnail-placeholder">No Image</div>
                            )}
                            <p><strong>Date:</strong> {gen.createdAt}</p>
                            <p><strong>Prompt:</strong> <em>{gen.prompt ? (gen.prompt.substring(0, 50) + (gen.prompt.length > 50 ? '...' : '')) : (gen.promptTemplateId ? `Template: ${gen.promptTemplateId}` : 'N/A')}</em></p>
                            <Link to={`/map/${gen.id}`} className="view-map-link" onClick={() => handleViewPreviousGeneration(gen.id)}>View Map</Link>
                        </div>
                    ))}
                </div>
            );
        }
    };

    // Fetch activity data when stravaId changes and user is available
    useEffect(() => {
        if (stravaId && user) {
            fetchActivityData(stravaId);
        }
    }, [stravaId, user]);

    // Track template selections
    const handleTemplateSelect = (template) => {
        posthog?.capture('prompt_template_selected', {
            template_id: template.id,
            template_name: template.name,
            page: 'generator'
        });
        setSelectedTemplateId(template.id);
        setPrompt(''); // Clear for additional notes or custom additions
    };

    // Track previous generation views
    const handleViewPreviousGeneration = (requestId) => {
        posthog?.capture('previous_generation_viewed', {
            request_id: requestId,
            source_page: 'generator'
        });
    };

    // Add admin check before the main return
    if (!user && !isLoading) {
        return (
            <div className="container request-generator-page">
                <h2>Generate New Map</h2>
                <p>Please <Link to="/">sign in</Link> to generate maps.</p>
            </div>
        );
    }

    if (user && !isAdmin) {
        return (
            <div className="container request-generator-page">
                <h2>Generate New Map</h2>
                <p>Admin access required to generate new maps. Please contact support if you need access.</p>
                <Link to="/history" style={{ display: 'block', marginTop: '20px' }}>&larr; Go to History</Link>
            </div>
        );
    }

    return (
        <div className="container request-generator-page">
            <Link to="/" style={{ display: 'block', marginBottom: '20px', textAlign: 'left' }}>&larr; Back to Landing</Link>

            {stravaIdFromParams && stravaIdFromParams !== '_new' && (
                <div className="previous-generations-section">
                    <h2>Previously Generated Maps for Activity {stravaIdFromParams}</h2>
                    {renderPreviousGenerations()}
                    <hr style={{ margin: '20px 0' }} />
                </div>
            )}

            <h1>Image Generation Request</h1>

            {/* Display activity map if we have the activity data */}
            {stravaId && stravaIdFromParams !== '_new' && (
                <div className="activity-preview-section" style={{ marginBottom: '30px' }}>
                    {isLoadingActivity ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>Loading activity details...</p>
                        </div>
                    ) : currentActivity ? (
                        <div style={{
                            display: 'flex',
                            gap: '20px',
                            alignItems: 'center',
                            backgroundColor: '#f8f9fa',
                            padding: '20px',
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0'
                        }}>
                            <div style={{ flexShrink: 0 }}>
                                <ActivityMap 
                                    activity={currentActivity} 
                                    width={300} 
                                    height={200} 
                                    onWaterAnalysis={handleWaterAnalysis}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{currentActivity.name}</h3>
                                <div style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                                    <div>📅 {new Date(currentActivity.start_date).toLocaleDateString()}</div>
                                    <div>📏 Distance: {(currentActivity.distance / 1000).toFixed(2)} km</div>
                                    <div>⏱️ Moving Time: {new Date(currentActivity.moving_time * 1000).toISOString().substr(11, 8)}</div>
                                    {currentActivity.total_elevation_gain && (
                                        <div>⛰️ Elevation Gain: {currentActivity.total_elevation_gain.toFixed(0)} m</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            color: '#666'
                        }}>
                            <p>Activity details not available</p>
                            <p style={{ fontSize: '12px', marginTop: '10px' }}>
                                Make sure you're signed in with the same Strava account that owns this activity
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Water Analysis Summary - only show to admins and when analysis is available */}
            {isWaterAnalyzed && waterSummary && isAdmin && (
                <div style={{
                    backgroundColor: '#e8f4f8',
                    border: '1px solid #b8dce8',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#2c5aa0' }}>
                            🌊 Water Content Analysis
                        </span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#2c5aa0', lineHeight: '1.4' }}>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>{waterSummary.percentage}% water content</strong> - {waterSummary.description}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                            <span>Water: {waterSummary.percentage}%</span>
                            <span>Showing: {filteredTemplates.length} styles</span>
                            <span>Recommended: {recommendedStyles.length} styles</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Form Section is always visible if user is logged in (or inputs disabled) */}
            <div className="form-section">
                <div>
                    <label htmlFor="stravaIdInput">Strava Activity ID:</label>
                    <input type="text" id="stravaIdInput" value={stravaId} onChange={(e) => setStravaId(e.target.value)} placeholder="E.g., from activity list or enter new" disabled={!user} />
                </div>
                <div>
                    <label htmlFor="promptTemplateSelect">Prompt Template (Optional):</label>
                    {isLoadingTemplates && <p>Loading templates...</p>}
                    {templateError && <p className="error">{templateError}</p>}
                    {!isLoadingTemplates && !templateError && (
                        <>
                            <select
                                id="promptTemplateSelect"
                                value={selectedTemplateId}
                                onChange={(e) => handleTemplateSelect(filteredTemplates.find(t => t.id === e.target.value))}
                                disabled={!user}
                                style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
                            >
                                <option value="">-- Select a Template --</option>
                                {isAdmin && <option value="custom">-- Write Custom Prompt Below --</option>}
                                {filteredTemplates.map(template => (
                                    <option key={template.id} value={template.id}>{template.name}</option>
                                ))}
                            </select>
                            {availableTemplates.length > 0 && filteredTemplates.length === 0 && (
                                <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', marginTop: '5px' }}>
                                    No styles available for this route's water content. All styles have been filtered based on coastal/land restrictions.
                                </p>
                            )}
                        </>
                    )}
                    {selectedTemplateId && selectedTemplateId !== 'custom' && currentTemplateDetails && (
                        <p style={{ fontSize: '0.9em', color: '#555', marginBottom: '10px', textAlign: 'left' }}>
                            <strong>Selected Template: {currentTemplateDetails.name}</strong><br />
                            <em>{currentTemplateDetails.description}</em>
                            {currentTemplateDetails.requiresStravaStats && <span style={{ color: 'orange', marginLeft: '10px' }}>(Requires Strava stats)</span>}
                        </p>
                    )}
                </div>
                {/* Only show prompt input for admins */}
                {isAdmin && (
                    <div>
                        <label htmlFor="promptInput">
                            {selectedTemplateId && selectedTemplateId !== 'custom' ? 'Additional Notes / Modifications to Template (Admin Only):' : 'Custom Prompt Text (Admin Only):'}
                        </label>
                        <textarea
                            id="promptInput"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={selectedTemplateId && selectedTemplateId !== 'custom' && selectedTemplateId !== '' ? "Enter any additions or changes to the selected template here..." : "Describe the desired image style..."}
                            disabled={!user}
                            style={{ minHeight: '100px' }} /* Reduced min-height if it's for additional notes */
                        />
                    </div>
                )}
                {!isAdmin && selectedTemplateId === 'custom' && (
                    <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>
                        Custom prompts are only available for admin users. Please select a template.
                    </p>
                )}
                <button onClick={handleSubmitRequest} disabled={!user || isLoading} className="submit-generation-button">
                    {isLoading ? 'Submitting...' : 'Submit Image Generation Request'}
                </button>
                {submitMessage && <p className="success text-center" style={{ marginTop: '10px' }}>{submitMessage}</p>}
                {submitError && <p className="error text-center" style={{ marginTop: '10px' }}>{submitError}</p>}
            </div>

            {/* Status section for the current request being tracked/viewed */}
            {currentRequestId && (
                <div className="status-section" style={{ marginTop: '30px' }}>
                    <hr />
                    <h2>Request Progress</h2>
                    <p><strong>ID:</strong> {currentRequestId}</p>
                    <p><strong>Status:</strong> {requestStatus}</p>

                    {/* Progress Bar */}
                    {progressPercentage > 0 && requestStatus !== 'openai_processing_complete' && (
                        <div className="progress-bar-container">
                            <div className="progress-bar">
                                <div
                                    className="progress-bar-fill"
                                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                                ></div>
                            </div>
                            <div className="progress-info">
                                <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
                                {/* Only show ETA during AI processing when estimates are meaningful */}
                                {requestStatus === 'openai_processing' && estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
                                    <span className="progress-eta">
                                        ETA: {formatDuration(estimatedTimeRemaining)}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {statusError && <p className="error"><strong>Error:</strong> {statusError}</p>}
                    {gpxFileLink && <p><strong>GPX File:</strong> <a href={gpxFileLink} target="_blank" rel="noopener noreferrer">Download GPX</a></p>}

                    {imageDisplayVisible && (
                        <div id="imageDisplayArea" style={{ marginTop: '15px' }}>
                            <p><strong>Image Comparison (Drag or use slider):</strong></p>
                            <div className="image-comparison-container" ref={imageComparisonDivRef} onMouseDown={startDrag} onTouchStart={startDrag}>
                                {originalImageSrc && <img src={originalImageSrc} alt="Original Map" />}
                                {generatedImageSrc && <img id="generatedImageDisplayForSlider" src={generatedImageSrc} alt="Generated AI Map" style={{ position: 'absolute', top: 0, left: 0, height: '100%', objectFit: 'cover' }} />}
                            </div>
                            <input type="range" id="imageOverlaySliderReact" min="0" max="100" value={sliderValue} onChange={handleSliderChange} style={{ width: '100%', marginTop: '10px' }} />
                            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '10px' }}>
                                <button onClick={() => navigate(`/map/${currentRequestId}`)} disabled={!currentRequestId}>Share This Comparison</button>
                                {generatedImageSrc && (
                                    <button onClick={handleDownloadAiImage} disabled={isDownloading} className="button-like-cta">
                                        {isDownloading ? 'Downloading...' : 'Download AI Image'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default RequestGeneratorPage; 